<script>
/* Vue native methods */
import { ref } from 'vue';

/* Data Strings */
import tooltipData from '../strings/tooltip-data';
import tooltipDataWl from '../strings/tooltip-data-wl';

/* Components */
import Multiselect from '@vueform/multiselect';

/* Helper functions */
import ApiHelper from '../helpers/apiHelper';

export default {
  components: {
    Multiselect
  },
  props: {},
  setup() {
    const zip = ref(null);
    const appPackages = ref({});
    const options = ref(null);
    const servicesID = ref(null);
    const zipCodeErrorVisible = ref(false);
    const zipCodeEmptyError = ref(false);
    const app_theme_brand = ref(BRAND_THEME);

    const tooltipContent = ref(tooltipData);
    const tooltipContentWl = ref(tooltipDataWl);

    const isZipValid = () => {
      const zipCode = zip.value?.value?.zip;

      if (!zipCode) {
        zipCodeEmptyError.value = true;
        return false;
      }

      return zipCode.length >= 5;
    }

    const createUrl = () => {
      const zipCode = zip.value?.value?.zip;
      let redirect_url = `https://shop.johanniter-portfolio.de/geschenkgutschein/hausnotruf/basis?zip=${zipCode}`;

      if (Object.keys(appPackages.value.packages).includes('komfort') || Object.keys(appPackages.value.packages).includes('premium')) {
        redirect_url = `https://shop.johanniter-portfolio.de/geschenkgutschein/hausnotruf/komfort?zip=${zipCode}`;
        return window.top.location.href = redirect_url;
      }

      return window.top.location.href = redirect_url;
    };

    const getPackages = async () => {
      if (!isZipValid()) return;

      const apiUrl = `${__API_URL__}/services?id=${servicesID.value}&insurance=false&mobile=false`;

      const apiHelper = new ApiHelper(apiUrl, 'GET', '');
      const response = await apiHelper.fetchData();

      if (response.status !== 200 && response.status !== 204) {
        console.error('Fetching zipcodes failed: ', response.status);
        return;
      }

      if (response.data.data.packages.basis == null) {
        response.data.data.packages.basis = { price: 23 };
      }

      appPackages.value = response.data.data;
      return createUrl();
    };

    // Handle zipcode API request each time user updates zipcode field value
    const handleACChange = async (data) => {
      if (data.length < 2) {
        return;
      }

      try {
        const apiUrl = `${__API_URL__}/zipcodes?zip=${data}`;
        const apiHelper = new ApiHelper(apiUrl, 'GET', '');
        const response = await apiHelper.fetchData();

        if (response.status !== 200 && response.status !== 204) {
          console.error('Fetching zipcodes failed: ', response.status);
          return;
        }

        // Map the response in to the format that is needed for autocomplete component
        options.value = response.data.data.map((item) => ({
          value: {
            id: item.id,
            zip: item.zip,
            city: item.city
          }
        }));
      } catch (error) {
        console.error('Error handling AC select:', error);
      }

      zip.value = data;
      zipCodeErrorVisible.value = false;
      zipCodeEmptyError.value = false;
    };
    // Handle zipcode selection from the dropdown
    const handleACSelect = async () => {
      zipCodeEmptyError.value = false;
      zipCodeErrorVisible.value = false;
      servicesID.value = zip?.value?.id || zip?.value?.value?.id
    };
    // Handle what happens when user clicks or taps out of the zipcode options dropdown
    const handleACClose = () => {
      zipCodeErrorVisible.value = false;

      // If there is only one option left then autoselect it
      if (options.value?.length == 1) {
        zip.value = options.value[0];
        handleACSelect();
      }

      // If there are two options left show error
      if (zip.value?.length == 5 && options.value?.length == 2) {
        zipCodeErrorVisible.value = true;
        zip.value = '';
      }

    };
    const handleACClear = () => {
      zip.value = '';
      zipCodeEmptyError.value = true;
    };

    return {
      zip,
      options,
      app_theme_brand,
      tooltipContent,
      tooltipContentWl,
      zipCodeErrorVisible,
      zipCodeEmptyError,
      getPackages,
      handleACChange,
      handleACSelect,
      handleACClose,
      handleACClear
    };

  }
};

</script>

<template>
  <div id="root"
       :class="app_theme_brand"
       class="root">
    <div class="root__body">
      <main id="stepper"
            class="stepper">
        <div class="stepper__step">
          <h4 class="stepper__title">An welchem Ort wird der Hausnotruf benötigt?</h4>
          <div v-tooltip="app_theme_brand == 'default' ? tooltipContent.s2 : tooltipContentWl.s2"
               class="icon icon--help" />
          <div class="zip-autocomplete">
            <Multiselect
              v-model="zip"
              id="zip-autocomplete"
              name="zip-autocomplete"
              input-type="text"
              autocomplete="off"
              placeholder="Hier Postleitzahl eingeben"
              :searchable="true"
              :options="options"
              no-options-text="Bitte geben Sie eine Postleitzahl ein"
              :resolve-on-load="false"
              :filterResults="false"
              track-by="value.id"
              :object="true"
              @search-change="handleACChange"
              @select="handleACSelect"
              @close="handleACClose"
              @clear="handleACClear"
            >
              <template v-slot:singlelabel="{ value }">
                <div class="multiselect-single-label">
                  <span> {{ value.value.zip }} </span>
                </div>
              </template>

              <template v-slot:option="{ option }">
                <span><strong> {{ option.value?.city }} </strong></span>
                <span> {{ option.value?.zip }} </span>
              </template>
            </Multiselect>

            <span
              v-if="zipCodeErrorVisible"
              class="icon icon--sort-up help is-danger">
              Zu Ihrer Postleitzahl gibt es mehr als einen Ort. Bitte wählen Sie einen Ort aus der Liste aus.
            </span>

            <span
              v-if="zipCodeEmptyError"
              class="icon icon--sort-up help is-danger">
              Bitte geben Sie die Postleitzahl ein und wählen Sie Ihren Ort aus der Liste aus.
            </span>

          </div>
        </div>

        <div class="nav">
          <button
            ref="stepForwards"
            class="button button--forwards"
            @click="getPackages()">Verfügbarkeit prüfen
          </button>
        </div>
      </main>
      <div v-show="app_theme_brand != 'default'" class="footer">Ein Service der Johanniter-Unfall-Hilfe e. V. - <a
        href="https://www.johanniter.de/datenschutz/" target="_blank">Datenschutzerklärung</a></div>
    </div>
  </div>
</template>

<style lang="scss">

.v-popper__inner {
  font-size: 14px;
}

</style>
